import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment.development';
import { BehaviorSubject, Observable, Subject, map, throwError, ReplaySubject, switchMap, forkJoin } from 'rxjs';
import { DataLesson } from '../shared/interfaces/lesson.interface';
import { DataEvaluation } from '../shared/interfaces/evaluation.interface';
import { NO_INTERCEPT } from '../shared/interceptors/auth-header.interceptor';

interface VideoInfo {
  id: string;
  platform: 'vimeo' | 'youtube' | 'unknown';
}

const HOST = environment.host;
const API_URL = environment.apiUrl;

@Injectable({
  providedIn: 'root'
})


export class ContentService {
  private _allActivitiesSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  allActivities$: Observable<any[]> = this._allActivitiesSubject.asObservable();
  private _filteredActivitiesSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  filteredActivities$: Observable<any[]> = this._filteredActivitiesSubject.asObservable();
  private _pathSubject: BehaviorSubject<String[]> = new BehaviorSubject<String[]>([]);
  activitiesPaths$: Observable<String[]> = this._pathSubject.asObservable();
  materials$ = new Subject<any[]>();
  bool$ = new Subject<boolean>();
  // materials$ = this.materialsSubject.asObservable();
  reloadNotifications$: Subject<boolean> = new Subject<boolean>();
  evaluationStatus$ = new Subject<boolean | undefined>();
  isEvaluationApproved: boolean = false;
  notificationsActivities$ = new Subject<any>();
  executeInLearningZone$ = new ReplaySubject<boolean>();
  activitiesForNotifications$ = new ReplaySubject<any[]>();
  lengthActivities: any;
  isLinkArrived: boolean = false;
  activityPaths: string[] = [];
  evaluations: any = [];
  hasPends: boolean = false;
  constructor(private http: HttpClient) { }

  //Get files
  getFileContent(id: string | undefined) {
    const URL = `${HOST}/api/file/file/${id}`;
    return this.http.get<any>(URL).pipe(
      map(resp => {
        const url = `${HOST}${resp.data.attributes.uri.url}`;
        const filename = resp.data.attributes.filename;
        return ({
          url,
          filename
        })
      })
    )
  }

  getFileUrl(fileId: number): Observable<any> {
    const context = new HttpContext().set(NO_INTERCEPT, true);
    const url = `${HOST}/serve-private-file/${fileId}`;
    return this.http.get(url, {
      responseType: 'blob', context: context, headers: new HttpHeaders({
        'Authorization': 'Basic YWRtaW46YWRtaW4='
      }),
    }).pipe(
      map(resp => {
        return window.URL.createObjectURL(resp);
      })
    );
  }

  getPrivateFileBlob(fileId: string): Observable<string> {
    // Uses default auth interceptor (user token)
    const url = `${HOST}/api/file/file/${fileId}`;
    // We first get the file metadata to find the real URI, OR we can try to download directly if we have a download endpoint.
    // However, the previous code suggested `/api/file/file/${id}` returns metadata.
    // Let's check getFileContent again.
    // getFileContent: returns {url, filename}. The url is `${HOST}${resp.data.attributes.uri.url}`.
    // That URL is likely the private file path.

    return this.http.get<any>(url).pipe(
      switchMap(resp => {
        const fileUrl = `${HOST}${resp.data.attributes.uri.url}`;
        return this.http.get(fileUrl, { responseType: 'blob' });
      }),
      map(blob => window.URL.createObjectURL(blob))
    );
  }

  //Routes
  getIdByPath(url: any) {
    const URL = `${HOST}/router/translate-path?path=${url}`;
    return this.http.get<any>(URL).pipe(
      map(resp => resp.entity.uuid)
    )
  }

  getActivitiesById(id: string) {
    const URL = `${API_URL}/activities/${id}`;
    return this.http.get<any>(URL).pipe(
      map(resp => resp.data)
    )
  }

  getActivitiesByLessonId(id: any): Observable<DataLesson[]> {
    const URL = `${API_URL}/activities?sort=-created&filter[field_lesson.id][value]=${id}`;
    return this.http.get<any>(URL).pipe(
      map(resp => resp.data)
    );
  }

  getActivitiesByCourseId(id: any): Observable<DataLesson[]> {
    const URL = `${API_URL}/activities?sort=-created&filter[field_course.id][value]=${id}`;
    return this.http.get<any>(URL).pipe(
      map(resp => resp.data)
    );
  }

  getActivitiesByModuleId(id: any): Observable<DataLesson[]> {
    const URL = `${API_URL}/activities?sort=-created&filter[field_module.id][value]=${id}`;
    return this.http.get<any>(URL).pipe(
      map(resp => resp.data)
    );
  }

  getMaterialsByLessonId(id: any): Observable<any> {
    const URL = `${API_URL}/materials?sort=-created&filter[field_lesson.id][value]=${id}`;
    const params = new HttpParams().set('include', 'field_file');

    return this.http.get<any>(URL, { params }).pipe(
      map(resp => resp.data)
    );
  }

  getEvaluationOfModuleById(id: any): Observable<DataEvaluation> {
    return this.http.get<any>(`${API_URL}/evaluation?sort=title&filter[field_module.id][value]=${id}`).pipe(
      map(resp => resp.data)
    );
  }

  getEvaluationById(id: any): Observable<DataEvaluation> {
    return this.http.get<any>(`${API_URL}/evaluation/${id}`).pipe(
      map(resp => resp.data)
    );
  }

  getQuestionById(id: any): Observable<any> {
    return this.http.get<any>(`${HOST}/api/question_bank/question_bank/${id}`).pipe(
      map(resp => resp.data)
    );
  }

  getAnswerById(id: any): Observable<any> {
    return this.http.get<any>(`${HOST}/api/paragraph/answer/${id}`).pipe(
      map(resp => resp.data)
    );
  }

  extractVideoId(vimeoLink: string | undefined): string {
    const regex = /vimeo\.com\/(\d+)/;
    const match = vimeoLink?.match(regex);
    return match ? match[1] : '';
  }

  extractVideoId2(videoLink: string | undefined): VideoInfo {
    const vimeoRegex = /vimeo\.com\/(\d+)/;
    const youtubeRegex = /youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/;

    let match = videoLink?.match(vimeoRegex);
    if (match) {
      return { id: match[1], platform: 'vimeo' };
    }

    match = videoLink?.match(youtubeRegex);
    if (match) {
      return { id: match[1], platform: 'youtube' };
    }

    return { id: '', platform: 'unknown' };
  }

  sendEvaluation(answers: any, csrf: string | any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Csrf-Token': csrf || null,
      }),
      withCredentials: true,
    };
    return this.http.post(`${HOST}/api/edutegia-ratings-result`, answers, httpOptions)
  }

  getGradesEvaluation(userId: number, evaluationId: number | undefined): Observable<any> {
    const URL = `${HOST}/api/rating/${userId}/${evaluationId}`;
    return this.http.get(URL);
  }

  deleteAttemptEvaluation(idAttempt: number): Observable<any> {
    const URL = `${HOST}/api/edutegia-ratings-delete`;
    return this.http.post(URL, { id: idAttempt })
  }

  uploadFile(file: File, csrf: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const httpOptions = {
      headers: new HttpHeaders({
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/octet-stream',
        'X-Csrf-Token': csrf || '',
        'Content-Disposition': `file; filename="${file.name}"`
      }),
      withCredentials: true,
    };
    return this.http.post<any>(`${HOST}/api/activities/activities/field_file`, formData, httpOptions).pipe(
      map(resp => resp.data)
    );
  }

  uploadActivity(formData: any, csrf: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Access-Control-Allow-Origin': '*',
        'Accept': 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
        'X-Csrf-Token': csrf || '',
      }),
      withCredentials: true,
    };
    return this.http.post(`${HOST}/api/activities/activities`, formData, httpOptions)
  }

  isActivitySent(userId: number, activityId: number): Observable<any> {
    const URL = `${HOST}/api/activities/${userId}/${activityId}`;
    return this.http.get(URL)
  }

  uploadImage(file: any, binaryFile: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/octet-stream',
        'Content-Disposition': `file; filename="${file.name}"`,
      }),
      withCredentials: true,
    };
    return this.http.post<any>(`${HOST}/file/upload/node/article/field_image?_format=json`, binaryFile, httpOptions)
  }

  getCertificate(idCourse: any, idStudent: any): Observable<any> {
    return this.http.get<any>(`${HOST}/api/certificates?c=${idCourse}&u=${idStudent}`)
  }

  sendContentViewed(form: any, csrf: any): Observable<any> {
    const url = `${HOST}/api/advance/advance`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/vnd.api+json',
        'X-Csrf-Token': csrf || '',
      }),
      withCredentials: true,
    };
    return this.http.post(url, form, httpOptions)
  }

  getMaterialview(uid: string, materialId: number): Observable<any> {
    const url = `${HOST}/api/advance/materials/${uid}/${materialId}`;
    return this.http.get(url)
  }

  getLessonview(uid: string, lessonId: number | undefined): Observable<any> {
    const url = `${HOST}/api/advance/lesson/${uid}/${lessonId}`;
    return this.http.get(url)
  }

  getCourseProgress(courseId: number | undefined, uid: string): Observable<any> {
    const url = `${HOST}/api/edutegia-advance/${courseId}/${uid}`;
    return this.http.get(url)
  }

  //propiedades

  setActivities(activities: any): void {
    this._allActivitiesSubject.next(activities)
  }

  setfilteredActivities(activities: any): void {
    this._filteredActivitiesSubject.next(activities)
  }

  setActivitiesPaths(paths: string[]): void {
    this._pathSubject.next(paths);
  }

  // setMaterialView(material:any):void{
  //   this.materialsSubject.next(material);
  // }
  //Manejador de errores
  private handleError(error: any) {
    let errorMessage = '';
    (error.error instanceof ErrorEvent) ? errorMessage = `Error: ${error.error.message}`
      : /* Error en el lado del servidor */ errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    // console.log(errorMessage);
    return throwError(() => {
      return errorMessage;
    });
  }


}
